import * as React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "../../lib/icons";
import { TextField } from "../TextField";
import { DropdownMenu, type DropdownMenuItem } from "../Dropdown/DropdownMenu";
import { BottomSheet } from "../BottomSheet";

export interface SelectOption {
  value: string | number;
  label?: string;
  description?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  isDisabled?: boolean;
  variant?: "default" | "bordered" | "primary" | "negative";
}

export interface SelectProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "prefix"> {
  /**
   * Options to display in the select dropdown
   */
  options?: SelectOption[];
  /**
   * Selected value (controlled)
   */
  value?: string | number;
  /**
   * Default selected value (uncontrolled)
   */
  defaultValue?: string | number;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string | number, option: SelectOption) => void;
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Label for the select
   */
  label?: string;
  /**
   * Helper text below the select
   */
  helperText?: string;
  /**
   * Error text (overrides helperText when present)
   */
  errorText?: string;
  /**
   * Success text (overrides helperText when present)
   */
  successText?: string;
  /**
   * Validation state for the select
   */
  validationState?: "none" | "positive" | "negative";
  /**
   * Whether the select is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the select is required
   */
  isRequired?: boolean;
  /**
   * Whether the select is optional
   */
  isOptional?: boolean;
  /**
   * Whether the select is in loading state
   */
  isLoading?: boolean;
  /**
   * Size of the select
   */
  size?: "small" | "medium" | "large";
  /**
   * Prefix element (icon or text)
   */
  prefix?: React.ReactNode;
  /**
   * Suffix element (icon or text)
   */
  suffix?: React.ReactNode;
  /**
   * Show clear button when value is selected
   */
  showClearButton?: boolean;
  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void;
  /**
   * Show the selected option's leadingIcon in the input box
   * Only shown when an option is selected and has a leadingIcon
   */
  showLeadingIcon?: boolean;
  /**
   * Custom class for the container
   */
  containerClassName?: string;
  /**
   * Custom class for the label
   */
  labelClassName?: string;
  /**
   * Custom class for the trigger button
   */
  triggerClassName?: string;
  /**
   * Custom class for the dropdown menu
   */
  menuClassName?: string;
  /**
   * Width of the dropdown menu
   */
  menuWidth?: "auto" | "full" | string;
  /**
   * Section heading for the dropdown
   */
  sectionHeading?: string;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Show search icon in empty state
   */
  emptyIcon?: React.ReactNode;
  /**
   * Info heading for tooltip
   */
  infoHeading?: string;
  /**
   * Info description for tooltip
   */
  infoDescription?: string;
  /**
   * Custom Link component to render on the right side
   * If provided, this will be rendered instead of the default Link with linkText
   */
  LinkComponent?: React.ReactNode;
  /**
   * Link text
   */
  linkText?: string;
  /**
   * Link href
   */
  linkHref?: string;
  /**
   * Link click handler
   */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const Select = React.forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      className,
      options = [],
      value: controlledValue,
      defaultValue,
      onChange,
      placeholder = "Select an option",
      label,
      helperText,
      errorText,
      successText,
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      isLoading = false,
      size = "medium",
      prefix,
      suffix,
      showClearButton = false,
      onClear,
      showLeadingIcon = false,
      containerClassName,
      labelClassName,
      triggerClassName,
      menuClassName,
      menuWidth = "full",
      sectionHeading,
      emptyTitle = "No options available",
      emptyDescription = "There are no options to select from.",
      emptyIcon,
      infoHeading,
      infoDescription,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<
      string | number | undefined
    >(defaultValue);
    const [isOpen, setIsOpen] = React.useState(false);
    const [dropdownPlacement, setDropdownPlacement] = React.useState<
      "top" | "bottom"
    >("bottom");
    const [isMobile, setIsMobile] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dropdownContainerRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current!);

    // Detect if we're on mobile (< 768px)
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      // Check on mount
      checkMobile();

      // Add resize listener
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const value =
      controlledValue !== undefined ? controlledValue : uncontrolledValue;

    // Find the selected option
    const selectedOption = options.find((opt) => opt.value === value);
    const hasValue = value !== undefined && value !== "";

    const handleOpenChange = (newOpen: boolean) => {
      if (!isDisabled && !isLoading) {
        setIsOpen(newOpen);
      }
    };

    const toggleOpen = () => {
      handleOpenChange(!isOpen);
    };

    const handleTriggerContainerClick = (
      event: React.MouseEvent<HTMLDivElement>
    ) => {
      if (isDisabled || isLoading) return;
      const target = event.target as HTMLElement;

      // Let clear/other explicit buttons and links behave normally.
      if (target.closest("button") || target.closest("a")) return;

      // Open the dropdown when clicking anywhere on the trigger area.
      handleOpenChange(true);
    };

    const handleSelect = (option: SelectOption) => {
      if (controlledValue === undefined) {
        setUncontrolledValue(option.value);
      }
      onChange?.(option.value, option);
      setIsOpen(false);
      inputRef.current?.focus();
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else {
        if (controlledValue === undefined) {
          setUncontrolledValue(undefined);
        }
        onChange?.("", {} as SelectOption);
      }
    };

    const updateDropdownPlacement = React.useCallback(() => {
      if (typeof window === "undefined") return;
      const trigger = containerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      const dropdownHeight = dropdownContainerRef.current
        ? dropdownContainerRef.current.offsetHeight
        : 0;

      if (dropdownHeight === 0) {
        setDropdownPlacement(spaceBelow >= spaceAbove ? "bottom" : "top");
        return;
      }

      if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
        setDropdownPlacement("bottom");
      } else {
        setDropdownPlacement("top");
      }
    }, []);

    const attachDropdownListeners = React.useCallback(() => {
      if (!isOpen) return;
      if (typeof window === "undefined") return;
      let rafId = requestAnimationFrame(updateDropdownPlacement);

      const handleUpdate = () => updateDropdownPlacement();
      window.addEventListener("resize", handleUpdate);
      window.addEventListener("scroll", handleUpdate, true);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", handleUpdate);
        window.removeEventListener("scroll", handleUpdate, true);
      };
    }, [isOpen, updateDropdownPlacement]);

    React.useEffect(() => {
      const detach = attachDropdownListeners();
      return () => {
        detach?.();
      };
    }, [attachDropdownListeners]);

    React.useEffect(() => {
      if (isOpen) {
        updateDropdownPlacement();
      }
    }, [isOpen, options.length, updateDropdownPlacement]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node) &&
          dropdownContainerRef.current &&
          !dropdownContainerRef.current.contains(event.target as Node)
        ) {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isDisabled || isLoading) return;

      if (!isOpen && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        setIsOpen(true);
        return;
      }

      if (isOpen) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const currentIndex = options.findIndex((opt) => opt.value === value);
          const nextIndex =
            event.key === "ArrowDown"
              ? Math.min(currentIndex + 1, options.length - 1)
              : Math.max(currentIndex - 1, 0);

          if (options[nextIndex] && !options[nextIndex].isDisabled) {
            handleSelect(options[nextIndex]);
          }
        }
      }
    };

    // Transform options to dropdown menu items
    const menuItems: DropdownMenuItem[] = options.map((option) => ({
      value: option.value,
      label: option.label ?? String(option.value),
      description: option.description,
      leadingIcon: option.leadingIcon,
      trailingIcon: option.trailingIcon,
      isDisabled: option.isDisabled,
      variant: option.variant,
      onClick: () => handleSelect(option),
    }));

    const widthStyle =
      menuWidth === "full" ? "100%" : menuWidth === "auto" ? "auto" : menuWidth;

    // Get the display value for the TextField
    const displayValue = isLoading ? "Loading..." : selectedOption?.label || "";

    // Build the prefix: either provided prefix or selected option's leadingIcon
    const displayPrefix = prefix
      ? prefix
      : showLeadingIcon && selectedOption?.leadingIcon
        ? selectedOption.leadingIcon
        : undefined;

    // Build the suffix: include both the optional suffix and the chevron icon
    const chevronIcon = (
      <Icon
        name={isOpen ? "chevronUp" : "chevronDown"}
        size={16}
        className="shrink-0 transition-colors"
      />
    );

    const displaySuffix = suffix ? (
      <>
        {suffix}
        {chevronIcon}
      </>
    ) : (
      chevronIcon
    );

    // Render dropdown menu content
    const renderDropdownContent = () => (
      <DropdownMenu
        items={menuItems}
        sectionHeading={sectionHeading}
        isEmpty={options.length === 0}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyIcon={emptyIcon}
        disableFooter={true}
        onClose={() => handleOpenChange(false)}
        className={menuClassName}
        width={isMobile ? "full" : widthStyle}
        unstyled={isMobile}
      />
    );

    return (
      <div
        ref={containerRef}
        className="relative w-full"
        onClickCapture={handleTriggerContainerClick}
      >
        <TextField
          ref={inputRef}
          label={label}
          helperText={helperText}
          errorText={errorText}
          successText={successText}
          validationState={validationState}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isOptional={isOptional}
          isLoading={isLoading}
          size={size}
          prefix={displayPrefix}
          suffix={displaySuffix}
          showClearButton={showClearButton && hasValue && !isLoading}
          onClear={handleClear}
          placeholder={placeholder}
          value={displayValue}
          readOnly
          containerClassName={cn("w-full", containerClassName)}
          labelClassName={labelClassName}
          className={cn("cursor-pointer", triggerClassName, className)}
          inputClassName="cursor-pointer"
          infoHeading={infoHeading}
          infoDescription={infoDescription}
          LinkComponent={LinkComponent}
          linkText={linkText}
          linkHref={linkHref}
          onLinkClick={onLinkClick}
          onClick={() => handleOpenChange(true)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          {...props}
        />

        {/* Mobile: BottomSheet, Desktop: Regular Dropdown */}
        {isOpen && !isDisabled && !isLoading && (
          isMobile ? (
            <BottomSheet
              isOpen={isOpen}
              onClose={() => handleOpenChange(false)}
              title={sectionHeading || label}
              variant="default"
              showDragHandle={true}
              closeOnOverlayClick={true}
              closeOnEscape={true}
              closeOnSwipeDown={true}
            >
              {renderDropdownContent()}
            </BottomSheet>
          ) : (
            <div
              ref={dropdownContainerRef}
              className={cn(
                "absolute z-50 left-0 right-0",
                dropdownPlacement === "bottom"
                  ? "top-full mt-1"
                  : "bottom-full mb-1"
              )}
            >
              {renderDropdownContent()}
            </div>
          )
        )}
      </div>
    );
  }
);

Select.displayName = "Select";
